import type { QuoteView } from "@contracts/index";
import { ApiRequestError } from "./api";

/** API calls for the public portal. Session is an HttpOnly cookie set by the API. */
async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: "same-origin",
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    let code = "http_error";
    let message = response.statusText;
    try {
      const payload = (await response.json()) as { error?: { code: string; message: string } };
      code = payload.error?.code ?? code;
      message = payload.error?.message ?? message;
    } catch {
      // not JSON
    }
    throw new ApiRequestError(response.status, code, message);
  }
  return (await response.json()) as T;
}

const post = <T>(path: string, body: unknown) => call<T>(path, { method: "POST", body: JSON.stringify(body) });

export const clientApi = {
  requestAccess: (email: string) => post<{ ok: true }>("/access/request", { email }),
  redeemMagicLink: (token: string) => post<{ email: string }>("/access/redeem", { token }),
  redeemQuoteLink: (token: string) => post<{ email: string; quoteId: string }>("/access/quote", { token }),
  logout: () => post<{ ok: true }>("/access/logout", {}),
  me: () => call<{ email: string }>("/client/me"),
  listQuotes: () => call<QuoteView[]>("/client/quotes"),
  getQuote: (id: string) => call<QuoteView>(`/client/quotes/${id}`),
  pdfUrl: (id: string) => `/api/client/quotes/${id}/pdf`,
  accept: (id: string, input: { name: string; position: string; selectedOptionalItemIds: string[] }) =>
    post<QuoteView>(`/client/quotes/${id}/accept`, input),
};

export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 401;
}
